import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AIRoleInScenario } from "../../../../lib/api/scenario";
import logo3 from "@/assets/images/ai/logo3.png";
import { useI18n } from "@/hooks/useI18n";
import { formatTextWithLineBreaks } from "../../../../utils/textUtils";
import {
  getMBTITypeByKey,
  getMBTIDisplayName,
} from "../../../../utils/mbtiTypes";
import { getLanguageName, LanguageCode } from "../../../../constants/languages";

// 角色数据接口（保留用于类型兼容）
interface PersonalityTraits {
  thinking_feeling: number;
  confident_chaotic: number;
  judging_exploring: number;
  extrovert_introvert: number;
  intuitive_observant: number;
}

interface RolesDisplayCardProps {
  roles?: AIRoleInScenario[];
  currentRoleId?: string;
  onRoleChange?: (roleId: string) => void;
  sceneLang?: string; // 场景语言，从场景数据中获取
}

// 角色详情弹窗组件
const RoleDetailModal: React.FC<{
  visible: boolean;
  role: AIRoleInScenario | null;
  onClose: () => void;
}> = ({ visible, role, onClose }) => {
  const { t } = useI18n();
  return (
      <AnimatePresence>
        {visible && role && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className='fixed inset-0 bg-black bg-opacity-60 flex items-end justify-center z-50 '
                onClick={(e) => e.target === e.currentTarget && onClose()}
            >
              <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                  className='bg-gradient-to-b from-white/80  via-white to-white relative w-full max-w-md rounded-t-xl max-h-[90vh] pt-20 border-t-2 border-t-white'
              >
                {/* 关闭按钮 */}
                <motion.button
                    onClick={onClose}
                    className='absolute top-4 right-4 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors duration-200 z-10'
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                >
                  <svg
                      className='w-5 h-5 text-gray-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </motion.button>

                {/* 角色头像和基本信息 */}
                <div className='text-center mb-6 '>
                  <img
                      src={role.role_data.avatar}
                      alt={role.role_data.name}
                      className='w-28 h-28 absolute -top-12 left-0 right-0 rounded-full object-cover mx-auto mb-4 bg-white border-4 border-[#A785F5]'
                  />
                  <h3 className='text-xl font-bold text-gray-800 mb-2'>
                    {role.role_data.name}
                  </h3>

                </div>
                <div className='p-3 overflow-auto max-h-[70vh]'>
                  {/* 角色背景 */}
                  <div className='p-4'>
                    <div className='flex items-center mb-3'>
                  <span className='text-gray-800  text-base font-medium relative role-before-tag'>
                    {t("ai.training.roleDescription")}
                  </span>
                    </div>
                    <div className='text-gray-600 text-sm leading-relaxed'>
                      <div
                          dangerouslySetInnerHTML={{
                            __html: formatTextWithLineBreaks(
                                role.role_data.description || ""
                            ),
                          }}
                      />
                    </div>
                  </div>

                  {/* 角色标签 */}
                  <div className='p-4'>
                    <div className='flex items-center mb-3'>
                  <span className='text-gray-800  text-base font-medium relative role-before-tag'>
                    {t("ai.training.roleTags")}
                  </span>
                    </div>
                    <div className=' text-sm leading-relaxed'>
                      {(role.role_data.tags || []).map(
                          (tag: string, index: number) => (
                              <span
                                  key={index}
                                  className='inline-block bg-[#ECE9FF] text-[#303133] px-3 py-1 rounded-sm mr-1 mb-2 text-sm whitespace-nowrap'
                              >
                        {tag}
                      </span>
                          )
                      )}
                    </div>
                  </div>
                  {/* 知识水平 */}
                  {(role.role_data.knowledge_level || []).length > 0 && (
                      <div className=' p-4 mb-4'>
                        <div className='flex items-center mb-3'>
                    <span className='text-gray-800 text-base font-medium relative role-before-tag'>
                      {t("ai.training.knowledgeLevel")}
                    </span>
                        </div>
                        <div className='flex flex-wrap gap-2'>
                          {(role.role_data.knowledge_level || []).map(
                              (level: string, index: number) => (
                                  <span
                                      key={index}
                                      className='bg-[#ECE9FF] text-[#303133] px-3 py-1 rounded-sm text-sm'
                                  >
                          {level}
                        </span>
                              )
                          )}
                        </div>
                      </div>
                  )}
                  {/* 性格类型 */}
                  {role.role_data.personality_type && (
                      <div className='p-4'>
                        <div className='flex items-center mb-3'>
                    <span className='text-gray-800 text-base font-medium relative role-before-tag'>
                      
                       {t("ai.training.性格类型")}
                    </span>
                          <span
                              style={{
                                background:
                                    "linear-gradient( 90deg, #82CBFF 0%, #A782FF 100%), linear-gradient( 171deg, #A782FF 0%, #828EFF 100%), linear-gradient( 170deg, #9D53FC 0%, #614EFB 49%, #778CFF 100%), linear-gradient( 270deg, #4E8FFD 0%, #A785F5 100%)",
                              }}
                              className=' text-white px-4 py-[2px] rounded-md text-xs mx-2'
                          >
                      {role.role_data.personality_type}
                    </span>
                        </div>
                        <div className='text-sm leading-relaxed'>
                          {getMBTITypeByKey(role.role_data.personality_type) && (
                              <div className='mt-2 text-gray-600 text-sm'>
                                {t(
                                  getMBTITypeByKey(role.role_data.personality_type)
                                      ?.aiCoachingApplication || ''
                                )}
                              </div>
                          )}
                        </div>
                      </div>
                  )}




                </div>
              </motion.div>
            </motion.div>
        )}
      </AnimatePresence>
  );
};

const RolesDisplayCard: React.FC<RolesDisplayCardProps> = ({
                                                             roles = [],
                                                             currentRoleId,
                                                             onRoleChange,
                                                             sceneLang = LanguageCode.ZH_CN, // 使用场景语言，默认为中文
                                                           }) => {
  const { t } = useI18n();
  // Mock数据
  const mockRoles: AIRoleInScenario[] = [];

  const displayRoles = roles.length > 0 ? roles : mockRoles;
  const [selectedRoleId, setSelectedRoleId] = useState(
      currentRoleId || displayRoles[0]?.scenario_role_id || ""
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [attemptTimes] = useState(0); // Mock数据
  const [shouldCenter, setShouldCenter] = useState(true);

  // 语言标签映射（使用统一的语言映射，带表情符号）
  const getLanguageLabel = (lang: string) => {
    return getLanguageName(lang, true);
  };

  // 检查内容是否需要居中（内容宽度小于容器宽度）
  useEffect(() => {
    const checkIfShouldCenter = () => {
      if (scrollRef.current) {
        const container = scrollRef.current;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;
        setShouldCenter(scrollWidth <= clientWidth);
      }
    };

    checkIfShouldCenter();
    window.addEventListener('resize', checkIfShouldCenter);
    return () => window.removeEventListener('resize', checkIfShouldCenter);
  }, [displayRoles]);

  const currentRole =
      displayRoles.find((role) => role.scenario_role_id === selectedRoleId) ||
      displayRoles[0];
  const currentIndex = displayRoles.findIndex(
      (role) => role.scenario_role_id === selectedRoleId
  );

  useEffect(() => {
    if (currentRoleId && currentRoleId !== selectedRoleId) {
      setSelectedRoleId(currentRoleId);
    }
  }, [currentRoleId, selectedRoleId]);

  const handleRoleChange = (roleId: string) => {
    setSelectedRoleId(roleId);
    onRoleChange?.(roleId);
  };

  // 滚动到指定角色（居中显示）
  const scrollToCenter = (index: number) => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const children = container.children;

      if (index >= 0 && index < children.length) {
        const targetElement = children[index] as HTMLElement;
        const containerWidth = container.clientWidth;
        const scrollLeft = container.scrollLeft;

        // 获取目标元素相对于容器的位置
        const targetLeft = targetElement.offsetLeft;
        const targetWidth = targetElement.offsetWidth;

        // 计算目标元素应该居中的位置
        const targetScrollLeft = targetLeft - containerWidth / 2 + targetWidth / 2;

        container.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: "smooth",
        });
      }
    }
  };

  const handlePrevRole = () => {
    const currentIndex = displayRoles.findIndex(
        (role) => role.scenario_role_id === selectedRoleId
    );
    const prevIndex =
        currentIndex > 0 ? currentIndex - 1 : displayRoles.length - 1;
    const prevRoleId = displayRoles[prevIndex].scenario_role_id;
    handleRoleChange(prevRoleId);
    scrollToCenter(prevIndex);
  };

  const handleNextRole = () => {
    const currentIndex = displayRoles.findIndex(
        (role) => role.scenario_role_id === selectedRoleId
    );
    const nextIndex =
        currentIndex < displayRoles.length - 1 ? currentIndex + 1 : 0;
    const nextRoleId = displayRoles[nextIndex].scenario_role_id;
    handleRoleChange(nextRoleId);
    scrollToCenter(nextIndex);
  };

  if (!currentRole) {
    return (
        <div className='text-center text-gray-500'>
          {t("ai.training.noRoleData")}
        </div>
    );
  }

  return (
      <div className=''>
        {/* 顶部角色展示区域 */}
        <div className='relative bg-gradient-to-b from-[#8295FF] to-[#AE7CFF] h-72 md:h-80 lg:h-96'>
          {/* 背景头像 */}
          <motion.div
              key={selectedRoleId}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className='absolute inset-0 flex items-center justify-center opacity-10'
          >
            <img
                src={currentRole.role_data.avatar}
                alt='background'
                className='w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96'
            />
          </motion.div>

          {/* 主要内容 */}
          <div className='relative z-10'>
            {/* 切换按钮和角色信息 */}
            <div className='flex items-center justify-between mb-4'>
              <motion.button
                  onClick={handlePrevRole}
                  className='absolute top-28 md:top-32 lg:top-40 left-6 md:left-8 lg:left-12 w-10 h-10 md:w-12 md:h-12 bg-white bg-opacity-80 rounded-full flex items-center justify-center shadow-md hover:bg-opacity-100 transition-all duration-200'
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
              >
                <svg
                    className='w-5 h-5 text-gray-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M15 19l-7-7 7-7'
                  />
                </svg>
              </motion.button>
              <motion.button
                  onClick={handleNextRole}
                  className='absolute top-28 md:top-32 lg:top-40 right-6 md:right-8 lg:right-12 w-10 h-10 md:w-12 md:h-12 bg-white bg-opacity-80 rounded-full flex items-center justify-center shadow-md hover:bg-opacity-100 transition-all duration-200'
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
              >
                <svg
                    className='w-5 h-5 text-gray-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5l7 7-7 7'
                  />
                </svg>
              </motion.button>
              {/* <div className='absolute max-w-32 top-2 md:top-3 lg:top-4 left-4 md:left-8 lg:left-12 rounded-lg bg-white/20 p-3 md:p-4 lg:p-5 text-center'>
                <motion.div key={selectedRoleId}>
                  <h3 className='text-sm md:text-xl lg:text-2xl truncate font-normal text-gray-800 mb-1'>
                    {currentRole.role_data.name}
                  </h3>
                </motion.div>
              </div> */}

              {/* 场景语言显示（只读） */}
              {/* <div className='absolute top-2 md:top-3 lg:top-4 right-2 md:right-3 lg:right-4'>
                <div className='bg-white/80 rounded-lg p-2 md:p-3 flex items-center gap-2'>
                <span className='text-sm md:text-base text-gray-700'>
                  {getLanguageLabel(sceneLang)}
                </span>
                </div>
              </div> */}
            </div>
          </div>

          {/* 底部角色列表 - 通过定位固定在下面 */}
          <div className='absolute bottom-0 left-0 right-0 bg-transparent z-20 pb-2'>
            <div className='absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-b from-white/0 to-[#f9fafb] -z-1 pointer-events-none'></div>
            <div className='relative w-full overflow-hidden'>
              <div
                  ref={scrollRef}
                  className={`flex gap-3 md:gap-4 lg:gap-6 px-4 md:px-6 lg:px-8 overflow-x-auto scrollbar-hide mb-2 pb-2 ${shouldCenter ? 'justify-center' : ''}`}
                  style={{
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    WebkitOverflowScrolling: "touch",
                  }}
              >
                {displayRoles.map((role, index) => {
                  const isSelected = role.scenario_role_id === selectedRoleId;

                  return (
                      <motion.div
                          key={role.scenario_role_id}
                          className='flex-shrink-0 cursor-pointer bg-white rounded-full p-1'
                          onClick={() => {
                            handleRoleChange(role.scenario_role_id);
                            scrollToCenter(index);
                          }}
                          animate={{
                            //   scale: isSelected ? 1.2 : 1,
                            opacity: isSelected ? 1 : 0.6,
                            //   y: isSelected ? -5 : 0
                          }}
                          transition={{ duration: 0.3 }}
                          whileTap={{ scale: 0.95 }}
                      >
                        <div
                            className={`relative ${
                                isSelected ? "ring-2 ring-purple-400 ring-offset-2" : ""
                            } rounded-full`}
                        >
                          <img
                              src={role.role_data.avatar}
                              alt={role.role_data.name}
                              className='w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full'
                          />
                          {isSelected && (
                              <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className='absolute -bottom-1 -right-1 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center'
                              >
                                <svg
                                    className='w-2 h-2 text-white'
                                    fill='currentColor'
                                    viewBox='0 0 20 20'
                                >
                                  <path
                                      fillRule='evenodd'
                                      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                                      clipRule='evenodd'
                                  />
                                </svg>
                              </motion.div>
                          )}
                        </div>
                      </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* 角色信息卡片 */}
        <div className='bg-transparent relative -top-2'>
          <motion.div
              key={selectedRoleId}
              initial={{ opacity: 0, y: -40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className='bg-gray-50 rounded-xl mx-2 p-2 md:p-4 lg:p-6  shadow-xl'
          >
            <div className='flex items-center m-2 justify-between'>
              <div className='flex-1'>
                <div className='flex items-center gap-2 mb-2'>
                  <h4 className='font-semibold text-gray-800 text-base md:text-lg lg:text-xl'>
                    {currentRole.role_data.name}
                  </h4>
                  {currentRole.role_data.personality_type && (
                      <span
                          style={{
                            background:
                                "linear-gradient( 90deg, #82CBFF 0%, #A782FF 100%), linear-gradient( 171deg, #A782FF 0%, #828EFF 100%), linear-gradient( 170deg, #9D53FC 0%, #614EFB 49%, #778CFF 100%), linear-gradient( 270deg, #4E8FFD 0%, #A785F5 100%)",
                          }}
                          className=' text-white px-4 py-[2px] rounded-md text-xs mx-2'
                      >
                    {currentRole.role_data.personality_type}
                  </span>
                  )}
                </div>
                <p
                    className='text-sm md:text-base text-gray-600 mb-3 line-clamp-3'
                    dangerouslySetInnerHTML={{
                      __html: formatTextWithLineBreaks(
                          currentRole.role_data.description || ""
                      ),
                    }}
                />
                {/* 标签 */}
                <div className='flex flex-wrap gap-2'>
                  {(currentRole.role_data.tags || []).map(
                      (tag: string, index: number) => (
                          <span
                              key={index}
                              className='bg-purple-100 text-purple-600 px-2 py-1 rounded-full text-xs md:text-sm'
                          >
                      {tag}
                    </span>
                      )
                  )}
                </div>
              </div>

              <motion.button
                  onClick={() => setShowDetailModal(true)}
                  className='ml-4 w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-purple-100 rounded-full flex items-center justify-center hover:bg-purple-200 transition-colors duration-200'
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
              >
                <svg
                    className='w-4 h-4 text-purple-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5l7 7-7 7'
                  />
                </svg>
              </motion.button>
            </div>
          </motion.div>
        </div>

        {/* 角色详情弹窗 */}
        <RoleDetailModal
            visible={showDetailModal}
            role={currentRole}
            onClose={() => setShowDetailModal(false)}
        />
      </div>
  );
};

export default RolesDisplayCard;
